import { useLayoutEffect, useRef, useState } from 'react';
import { CallToActionContainerProps } from '@/components/common/callToAction/CallToActionContainer';
import ModalFooter from '@/components/layout/modals/ModalFooter';
import ModalHeadlineV2, { ModalHeadlineV2Props } from '@/components/layout/modals/ModalHeadlineV2';

import ModalFooterDs, { ModalFooterDsProps } from '@/components/layout/modals/ModalFooter.ds';
import ModalHeaderDs, { ModalHeaderDsProps } from '@/components/layout/modals/ModalHeader.ds';

import styles from './FullScreenContainerV2.module.scss';

type FullScreenContainerV2Props = {
  children?: React.ReactNode;
  onClose?: (event: any) => void;
  overlay?: boolean;
  classNames?: {
    container?: string;
    content?: string;
  };
  animationClass?: string;
  padding?: 'xl' | 'md' | 'none';
  headline?: ModalHeadlineV2Props;
  footer?: CallToActionContainerProps[];
  isOpen?: boolean
  designSystemFooter?: ModalFooterDsProps;
  designSystemHeader?: ModalHeaderDsProps;
  loading?: boolean;
}

export const FullScreenContainerV2 = ({
  children, onClose, overlay, classNames, animationClass, headline, footer,
  isOpen = false, padding = 'xl', designSystemFooter, designSystemHeader, loading = false
}: FullScreenContainerV2Props) => {
  const bodyContainerRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  useLayoutEffect(() => {
    if (bodyContainerRef.current) {
      const visibleHeight = bodyContainerRef.current.clientHeight;
      const scrollHeight = bodyContainerRef.current.scrollHeight;
      setShouldScroll(scrollHeight > visibleHeight);
    }
  }, [bodyContainerRef.current]);

  const overlayClassNames = [
    styles['container-overlay'],
    overlay && styles['color-overlay']
  ].filter(Boolean).join(' ');

  const paddingStyle = (() => {
    switch (padding) {
      case 'md':
        return styles['padding-md'];
      case 'xl':
        return styles['padding-xl'];
      default: return '';
    }
  })();

  const containerClassNames = [
    styles.container,
    classNames?.container,
    animationClass,
    paddingStyle
  ].join(' ');

  const contentClassNames = [
    styles.content,
    classNames?.content || paddingStyle
  ].filter(Boolean).join(' ');

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && onClose) {
      onClose(null);
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', handleKeyDown);
  }

  const loadingStyle = [
    classNames?.container,
    styles['modal-overlay-spinner']
  ].filter(Boolean).join(' ');

  return (
    <>
      <div
        className={overlayClassNames}
        onClick={() => {
          onClose?.(null);
        }}
        data-test-id="full-screen-container-overlay"
        data-rbf="full-screen-container-overlay"
      />

      <dialog
        className={containerClassNames}
        data-test-id="full-screen-container"
        data-rbf="full-screen-container"
        open={isOpen}
      >
        {loading && (
          <div className={loadingStyle}>
            <div className={styles.spinner} />
          </div>
        )}
        {!!designSystemHeader && (<ModalHeaderDs {...designSystemHeader} />)}
        {headline && (<ModalHeadlineV2 {...headline} />)}
        <div className={contentClassNames} ref={bodyContainerRef}>
          {children}
        </div>
        {footer && (<ModalFooter footer={footer} />)}
        {!!designSystemFooter && (<ModalFooterDs {...designSystemFooter} sticky={shouldScroll} />)}
      </dialog>
    </>
  );
};

export default FullScreenContainerV2;
